// Search in the distance readout at the top of the viewer: every object in
// the Objects index, a key away (/), without opening the controls panel.
// Empty, it lists the whole index by section to browse; typed, it narrows
// to the best matches. The catalogue and what a pick does come from
// main.js (buildObjectBrowserCatalog, goToCatalogItem), so this and the
// Objects panel always agree.

const MAX_MATCHES = 40;

function score(item, sectionTitle, q) {
  const label = item.label.toLowerCase();
  if (label === q) return 0;
  if (label.startsWith(q)) return 1;
  if (label.split(/[\s()-]+/).some((w) => w.startsWith(q))) return 2;
  if (label.includes(q)) return 3;
  if (`${item.detail} ${sectionTitle}`.toLowerCase().includes(q)) return 4;
  return -1;
}

export function initGoSearch({ getCatalog, onPick }) {
  const root = document.getElementById('go-search');
  const openBtn = document.getElementById('go-search-open');
  const input = document.getElementById('go-search-input');
  const list = document.getElementById('go-search-results');
  if (!root || !openBtn || !input || !list) return null;

  let rows = [];
  let active = -1;

  function setActive(i) {
    // -1 with nothing chosen clears; from the first row it wraps.
    if (!rows.length || (i < 0 && active < 0)) {
      active = -1;
      rows.forEach((r) => r.el.classList.remove('is-active'));
      input.removeAttribute('aria-activedescendant');
      return;
    }
    active = (i + rows.length) % rows.length;
    rows.forEach((r, k) => r.el.classList.toggle('is-active', k === active));
    const el = rows[active].el;
    input.setAttribute('aria-activedescendant', el.id);
    el.scrollIntoView({ block: 'nearest' });
  }

  function addRow(parent, item, query) {
    const el = document.createElement('li');
    el.id = `go-search-opt-${rows.length}`;
    el.className = 'go-search-item';
    el.setAttribute('role', 'option');
    const name = document.createElement('span');
    name.className = 'go-search-name';
    name.textContent = item.label;
    const detail = document.createElement('span');
    detail.className = 'go-search-detail';
    detail.textContent = item.detail || '';
    el.append(name, detail);
    const index = rows.length;
    // pointerdown keeps the input focused, so the list does not close
    // under the click before it lands.
    el.addEventListener('pointerdown', (e) => e.preventDefault());
    el.addEventListener('click', () => pick(rows[index].item, query));
    el.addEventListener('pointermove', () => { if (active !== index) setActive(index); });
    parent.appendChild(el);
    rows.push({ el, item });
  }

  function render() {
    const q = input.value.trim().toLowerCase();
    const sections = getCatalog();
    list.replaceChildren();
    rows = [];
    if (q) {
      const matches = [];
      sections.forEach((s) => s.items.forEach((item) => {
        const r = score(item, s.title, q);
        if (r >= 0) matches.push({ item, r });
      }));
      matches.sort((a, b) => a.r - b.r);
      if (!matches.length) {
        const none = document.createElement('li');
        none.className = 'go-search-empty';
        none.textContent = `Nothing called "${input.value.trim()}"`;
        list.appendChild(none);
      } else {
        matches.slice(0, MAX_MATCHES).forEach(({ item }) => addRow(list, item, q));
      }
    } else {
      sections.forEach((s) => {
        if (!s.items.length) return;
        const group = document.createElement('li');
        group.className = 'go-search-group';
        group.setAttribute('role', 'presentation');
        const heading = document.createElement('p');
        heading.className = 'go-search-heading';
        heading.textContent = s.title;
        const ul = document.createElement('ul');
        ul.setAttribute('role', 'group');
        ul.setAttribute('aria-label', s.title);
        group.append(heading, ul);
        list.appendChild(group);
        s.items.forEach((item) => addRow(ul, item, ''));
      });
    }
    list.scrollTop = 0;
    active = -1;
    setActive(q ? 0 : -1);
  }

  function open() {
    if (root.classList.contains('is-open')) return;
    root.classList.add('is-open');
    openBtn.setAttribute('aria-expanded', 'true');
    input.setAttribute('aria-expanded', 'true');
    render();
    input.focus({ preventScroll: true });
  }

  function close({ restoreFocus = false } = {}) {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    openBtn.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-expanded', 'false');
    input.value = '';
    list.replaceChildren();
    rows = [];
    active = -1;
    if (restoreFocus) openBtn.focus({ preventScroll: true });
    else input.blur();
  }

  function pick(item, query) {
    close();
    onPick(item, query);
  }

  openBtn.addEventListener('click', (e) => { e.stopPropagation(); open(); });
  input.addEventListener('input', render);
  input.addEventListener('keydown', (e) => {
    // The viewer's own shortcuts (Escape closes the info panel, letters
    // switch modes) must not fire while typing a name.
    e.stopPropagation();
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active < 0 ? rows.length - 1 : active - 1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[active] || rows[0];
      if (row) pick(row.item, input.value.trim().toLowerCase());
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close({ restoreFocus: true });
    }
  });
  input.addEventListener('blur', () => {
    // Let a click on a result land first.
    setTimeout(() => { if (document.activeElement !== input) close(); }, 120);
  });
  root.addEventListener('click', (e) => e.stopPropagation());
  root.addEventListener('pointerdown', (e) => e.stopPropagation());
  root.addEventListener('wheel', (e) => e.stopPropagation(), { passive: true });

  // "/" opens it from anywhere in the viewer, unless a field has focus or
  // the readout is hidden (fly mode, rocketry, photo mode...).
  window.addEventListener('keydown', (e) => {
    if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target;
    const tag = t && t.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
    if (!root.offsetParent) return;
    e.preventDefault();
    open();
  });

  return { open, close };
}
